import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Button, CircularProgress, Alert, Select, MenuItem, Checkbox,
    FormControl, InputLabel, Grid, Rating, Chip, IconButton, Dialog, DialogTitle,
    DialogContent, DialogActions, Collapse, Card, CardContent
} from '@mui/material';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';
import { getAllReviewsForAdmin, updateReviewStatus } from '../api/admin';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
}));

const DetailSection = ({ title, children }) => (
    <Box sx={{ mb: 2 }}>
        <Typography
            variant="subtitle2"
            sx={{
                fontWeight: "bold",
                fontSize: "0.9rem",
                color: "#11224d",
                mb: 0.5,
                borderBottom: '1px solid #ddd',
                pb: 0.5,
            }}
        >
            {title}
        </Typography>
        {children}
    </Box>
);

const ReviewRow = ({ review, isSelected, selectionMode, onSelect, onUpdateStatus, onDelete }) => {
    const [open, setOpen] = useState(false);

    const handleRowClick = () => {
        if (selectionMode) {
            onSelect(review.id);
        } else {
            setOpen(!open);
        }
    };

    return (
        <>
            <TableRow
                hover
                onClick={handleRowClick}
                role="checkbox"
                aria-checked={isSelected}
                selected={isSelected}
                sx={{ cursor: 'pointer', '& > td': { borderBottom: 'none' } }}
            >
                <TableCell padding="checkbox">
                    {selectionMode ? (
                        <Checkbox checked={isSelected} onClick={(e) => e.stopPropagation()} onChange={() => onSelect(review.id)} />
                    ) : (
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setOpen(!open); }}>
                            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                    )}
                </TableCell>
                <TableCell>{dayjs(review.created_at).format('MM/DD/YYYY')}</TableCell>
                <TableCell>
                    <Typography variant="body2" fontWeight="bold">{review.reviewer_company_name || 'N/A'}</Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'capitalize' }}>{review.reviewer_user_role}</Typography>
                </TableCell>
                <TableCell>
                     <Typography variant="body2" fontWeight="bold">{review.reviewee_company_name || 'N/A'}</Typography>
                     <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'capitalize' }}>{review.reviewee_user_role || 'Target'}</Typography>
                </TableCell>
                <TableCell>
                    <Rating value={Number(review.rating)} readOnly size="small" />
                </TableCell>
                <TableCell align="center">
                    {review.is_approved === null ? (
                        <Chip label="Pending" color="warning" size="small" sx={{ fontWeight: 600, height: 24 }} />
                    ) : review.is_approved ? (
                        <Chip label="Approved" color="success" size="small" sx={{ fontWeight: 600, height: 24 }} />
                    ) : (
                        <Chip label="Rejected" color="error" size="small" sx={{ fontWeight: 600, height: 24 }} />
                    )}
                </TableCell>
                <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    {!selectionMode && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                             {review.is_approved !== true && (
                                <IconButton size="small" color="success" onClick={() => onUpdateStatus(review.id, true)} title="Approve">
                                    <CheckCircleIcon fontSize="small" />
                                </IconButton>
                            )}
                            {review.is_approved !== false && (
                                <IconButton size="small" color="error" onClick={() => onUpdateStatus(review.id, false)} title="Reject">
                                    <CancelIcon fontSize="small" />
                                </IconButton>
                            )}
                            <IconButton size="small" onClick={() => onDelete([review.id])} title="Delete">
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    )}
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Card sx={{ m: 2, border: '1px solid #e0e0e0', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                            <CardContent>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={4}>
                                        <DetailSection title="Review By (Reviewer)">
                                            <Typography variant="body2"><strong>Company:</strong> {review.reviewer_company_name || 'N/A'}</Typography>
                                            <Typography variant="body2" sx={{ textTransform: 'capitalize' }}><strong>Role:</strong> {review.reviewer_user_role}</Typography>
                                            <Typography variant="body2"><strong>Email:</strong> {review.reviewer_email || 'N/A'}</Typography>
                                        </DetailSection>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <DetailSection title="Review For (Target)">
                                            <Typography variant="body2"><strong>Company:</strong> {review.reviewee_company_name || 'N/A'}</Typography>
                                            <Typography variant="body2" sx={{ textTransform: 'capitalize' }}><strong>Role:</strong> {review.reviewee_user_role || 'N/A'}</Typography>
                                            <Typography variant="body2"><strong>Email:</strong> {review.reviewee_email || 'N/A'}</Typography>
                                        </DetailSection>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <DetailSection title="Review Content">
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                <Typography variant="body2" sx={{ mr: 1 }}><strong>Rating:</strong></Typography>
                                                <Rating value={Number(review.rating)} readOnly size="small" />
                                            </Box>
                                            <Typography variant="body2" sx={{ mb: 1 }}><strong>Date:</strong> {dayjs(review.created_at).format('MMMM D, YYYY h:mm A')}</Typography>
                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', bgcolor: '#f9f9f9', p: 1.5, borderRadius: 1, border: '1px solid #eee', fontStyle: 'italic' }}>
                                                "{review.comment}"
                                            </Typography>
                                        </DetailSection>
                                    </Grid>
                                </Grid>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
                                    {review.is_approved !== true && (
                                        <Button variant="contained" color="success" size="small" startIcon={<CheckCircleIcon />} onClick={() => onUpdateStatus(review.id, true)}>
                                            Approve Review
                                        </Button>
                                    )}
                                    {review.is_approved !== false && (
                                        <Button variant="contained" color="error" size="small" startIcon={<CancelIcon />} onClick={() => onUpdateStatus(review.id, false)}>
                                            Reject Review
                                        </Button>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

const ReviewManagement = () => {
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState('all');
    const [selectionMode, setSelectionMode] = useState(false);
    const [selected, setSelected] = useState([]);
    const [confirmDialog, setConfirmDialog] = useState({ open: false, ids: [], message: '' });

    const fetchReviews = async () => {
        try {
            setLoading(true);
            const data = await getAllReviewsForAdmin();
            setReviews(data);
        } catch (err) {
            setError(err.message || 'Failed to fetch reviews.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReviews();
    }, []);

    const handleUpdateStatus = async (reviewId, isApproved) => {
        try {
            const updatedReview = await updateReviewStatus(reviewId, isApproved);
            setReviews(reviews.map(r => (r.id === reviewId ? updatedReview : r)));
        } catch (err) {
            setError('Failed to update review status.');
        }
    };

    const handleSelect = (reviewId) => {
        const selectedIndex = selected.indexOf(reviewId);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, reviewId);
        } else {
            newSelected = selected.filter(id => id !== reviewId);
        }
        setSelected(newSelected);
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            const newSelecteds = filteredReviews.map((r) => r.id);
            setSelected(newSelecteds);
            return;
        }
        setSelected([]);
    };

    const handleDeleteRequest = (reviewIds) => {
        const message = reviewIds.length > 1
            ? `Are you sure you want to delete these ${reviewIds.length} reviews? This action is permanent.`
            : 'Are you sure you want to delete this review? This action is permanent.';

        setConfirmDialog({
            open: true,
            ids: reviewIds,
            message: message,
        });
    };

    const handleConfirmDelete = async () => {
        const idsToDelete = confirmDialog.ids;
        if (idsToDelete.length === 0) return;

        try {
            const token = localStorage.getItem('jwtToken');
            const headers = { Authorization: `Bearer ${token}` };

            if (idsToDelete.length === 1) {
                await axios.delete(`${API_BASE_URL}/api/admin/reviews/${idsToDelete[0]}`, { headers });
            } else {
                await axios.post(`${API_BASE_URL}/api/admin/reviews/delete-multiple`, { ids: idsToDelete }, { headers });
            }

            fetchReviews();
        } catch (err) {
            console.error('Failed to delete review(s):', err);
            setError('Failed to delete review(s).');
        } finally {
            setConfirmDialog({ open: false, ids: [], message: '' });
            setSelectionMode(false);
            setSelected([]);
        }
    };

    const filteredReviews = useMemo(() => {
        if (filter === 'all') return reviews;
        if (filter === 'pending') {
            return reviews.filter(review => review.is_approved === null);
        }
        if (filter === 'approved') {
            return reviews.filter(review => review.is_approved === true);
        }
        return reviews.filter(review => review.is_approved === false);
    }, [reviews, filter]);

    if (loading) return <CircularProgress />;
    if (error) return <Alert severity="error">{error}</Alert>;

    return (
        <Box>
            <StyledPaper>
                <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Filter by Status</InputLabel>
                            <Select
                                value={filter}
                                label="Filter by Status"
                                onChange={(e) => setFilter(e.target.value)}
                            >
                                <MenuItem value="all">All</MenuItem>
                                <MenuItem value="pending">Pending</MenuItem>
                                <MenuItem value="approved">Approved</MenuItem>
                                <MenuItem value="rejected">Rejected</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item>
                        {selectionMode ? (
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                    variant="contained"
                                    color="error"
                                    size="small"
                                    startIcon={<DeleteOutlineIcon />}
                                    onClick={() => handleDeleteRequest(selected)}
                                    disabled={selected.length === 0}
                                >
                                    Delete ({selected.length})
                                </Button>
                                <Button size="small" onClick={() => { setSelectionMode(false); setSelected([]); }}>Cancel</Button>
                            </Box>
                        ) : (
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => setSelectionMode(true)}
                                disabled={reviews.length === 0}
                            >
                                Select
                            </Button>
                        )}
                    </Grid>
                </Grid>
            </StyledPaper>

            <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 120px)' }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox" sx={{ backgroundColor: '#11224d', color: 'white' }}>
                                {selectionMode ? (
                                    <Checkbox
                                        indeterminate={selected.length > 0 && selected.length < filteredReviews.length}
                                        checked={filteredReviews.length > 0 && selected.length === filteredReviews.length}
                                        onChange={handleSelectAll}
                                        sx={{ color: 'white' }}
                                    />
                                ) : (
                                    <Box sx={{ width: 24 }} /> 
                                )}
                            </TableCell>
                            <TableCell sx={{ color: 'white', backgroundColor: '#11224d' }}>Date</TableCell>
                            <TableCell sx={{ color: 'white', backgroundColor: '#11224d' }}>Review By</TableCell>
                            <TableCell sx={{ color: 'white', backgroundColor: '#11224d' }}>Review For</TableCell>
                            <TableCell sx={{ color: 'white', backgroundColor: '#11224d' }}>Rating</TableCell>
                            <TableCell sx={{ color: 'white', textAlign: 'center', backgroundColor: '#11224d' }}>Status</TableCell>
                            <TableCell sx={{ color: 'white', textAlign: 'right', backgroundColor: '#11224d' }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredReviews.map(review => (
                            <ReviewRow
                                key={review.id}
                                review={review}
                                isSelected={selected.indexOf(review.id) !== -1}
                                selectionMode={selectionMode}
                                onSelect={handleSelect}
                                onUpdateStatus={handleUpdateStatus}
                                onDelete={handleDeleteRequest}
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog
                open={confirmDialog.open}
                onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
            >
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <Typography>{confirmDialog.message}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}>Cancel</Button>
                    <Button onClick={handleConfirmDelete} color="error" autoFocus>Delete</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ReviewManagement;
